/**
 * instrumentSettingsConfig.js
 *
 * Metadata for every standardised rating scale / questionnaire instrument
 * that can be assigned to patients and informants.  Organised into three
 * groups that mirror the Firestore instruments config stored on each
 * organisation document at organisations/{orgId}.instruments.
 *
 * All groups use the same toggle UI.  Patient scales map to a boolean
 * map (`patientScales: { key: true/false }`).  Informant groups map to
 * a single string field (`informantAdulthoodInstrument`, etc.) - only
 * one instrument can be active per life stage, enforced via
 * `mutuallyExclusiveWith`.
 *
 * Per-pathway configuration:
 *   The instruments field in Firestore is module-keyed:
 *     instruments: { adultAdhd: { patientScales, ... }, adultAutism: { patientScales } }
 *   MODULE_SCALE_SETS controls which patient scales are available per pathway.
 *   MODULE_INSTRUMENT_GROUPS controls which group sections are rendered per pathway.
 *
 *   CYP ADHD is not a toggle set. The child does not complete rating scales.
 *   SNAP-IV 26 is completed by the parent or carer and the school
 *   representative, and the form registry assigns it for every organisation.
 *   CYP_RATING_SCALE_OPTIONS records that fact so the settings screen does
 *   not show "0 rating scales" for a pathway that always uses SNAP-IV.
 *
 * Each entry provides:
 *   - key:                   Firestore field key (matches the varName in form data models)
 *   - label:                 Short human-readable name shown next to the toggle
 *   - description:           Tooltip text explaining what the instrument measures
 *   - mutuallyExclusiveWith: (optional) key of a scale that cannot be enabled simultaneously
 *   - restrictToOrgs:        (optional) array of org names; hides the item from other orgs
 */

// ─── Patient Scales ──────────────────────────────────────────────────────
// Self-report questionnaires completed by the patient as part of
// pre-assessment information gathering.  Orgs can enable/disable
// individual scales to tailor the assessment battery.
export const PATIENT_SCALE_OPTIONS = [
  {
    key: 'asrs',
    label: 'ASRS (Current ADHD Symptoms)',
    description:
      'Adult ADHD Self-Report Scale - 18-item ADHD symptom self-assessment.',
    mutuallyExclusiveWith: 'baarsIVCurrent',
  },
  {
    key: 'baarsIVCurrent',
    label: 'BAARS-IV (Current ADHD Symptoms)',
    description:
      'Barkley Adult ADHD Rating Scale - assesses current ADHD symptoms over the past 6 months.',
    mutuallyExclusiveWith: 'asrs',
  },
  {
    key: 'baarsIVChildhood',
    label: 'BAARS-IV (Childhood ADHD Symptoms)',
    description:
      'Barkley Adult ADHD Rating Scale - retrospective recall of ADHD symptoms during childhood (ages 5–12).',
  },
  {
    key: 'phq9',
    label: 'PHQ-9 (Depression Symptoms)',
    description:
      'Patient Health Questionnaire - 9-item depression screening tool widely used in clinical settings.',
  },
  {
    key: 'gad7',
    label: 'GAD-7 (Anxiety Symptoms)',
    description:
      'Generalised Anxiety Disorder Questionnaire - 7-item anxiety screening tool.',
  },
  {
    key: 'aq10',
    label: 'AQ-10 (Autism Symptoms)',
    description:
      'Autism Spectrum Quotient - 10-item brief autism screening questionnaire.',
    mutuallyExclusiveWith: 'aq50',
  },
  {
    key: 'aq50',
    label: 'AQ-50 (Autism Symptoms - Extended)',
    description:
      'Autism Spectrum Quotient - 50-item full autism screening questionnaire.',
    mutuallyExclusiveWith: 'aq10',
  },
  {
    key: 'ess',
    label: 'ESS (Epworth Sleepiness Scale)',
    description:
      'Epworth Sleepiness Scale - measures daytime sleepiness to rule out sleep disorders.',
  },
  {
    key: 'mdq',
    label: 'MDQ (Mood Disorder Symptoms)',
    description:
      'Mood Disorder Questionnaire - screens for bipolar spectrum disorders.',
  },
  {
    key: 'ybocs',
    label: 'Y-BOCS (Obsessive Compulsive Symptoms)',
    description:
      'Yale-Brown Obsessive Compulsive Scale - assesses OCD symptom severity.',
  },
  {
    key: 'core10',
    label: 'CORE-10 (Psychological Distress)',
    description:
      'Clinical Outcomes in Routine Evaluation - 10-item general psychological distress measure.',
  },
  {
    key: 'cnst',
    label: 'Combined Neurodevelopmental Screening Tool',
    description:
      'Combined Neurodevelopmental Screening Tool - screens for co-occurring neurodevelopmental conditions.',
    restrictToOrgs: ['ADHD and Autism Associates'],
  },
  {
    key: 'spat',
    label: 'Sensory Profile Assessment Tool',
    description:
      'Sensory Profile Assessment Tool - evaluates sensory processing patterns.',
    restrictToOrgs: ['ADHD and Autism Associates'],
  },
  {
    key: 'catq',
    label: 'CAT-Q (Camouflaging Autistic Traits)',
    description:
      'Camouflaging Autistic Traits Questionnaire - 25-item self-report measure of social camouflaging strategies.',
  },
  {
    key: 'auditc',
    label: 'AUDIT-C (Alcohol Use)',
    description:
      'Alcohol Use Disorders Identification Test - Consumption (3-item brief screen for hazardous drinking).',
  },
];

// ─── Informant Adulthood Instrument Options ──────────────────────────────
// Only one instrument can be active per life stage.  Mutual exclusivity
// is enforced by `mutuallyExclusiveWith` - toggling one on disables the
// other, and the UI prevents turning off the last remaining option.
export const INFORMANT_ADULTHOOD_OPTIONS = [
  {
    key: 'baarsIVCurrentInformant',
    label: 'BAARS-IV Informant (Current ADHD Symptoms)',
    description:
      'Informant report on current ADHD symptoms observed over the past 6 months using the Barkley scale.',
    mutuallyExclusiveWith: 'asrsOAdulthoodInformant',
  },
  {
    key: 'asrsOAdulthoodInformant',
    label: 'ASRS-O Informant (Current ADHD Symptoms)',
    description:
      'Informant report on current ADHD symptoms using the ASRS Observer scale.',
    mutuallyExclusiveWith: 'baarsIVCurrentInformant',
  },
];

// ─── Informant Childhood Instrument Options ──────────────────────────────
// Same mutual-exclusivity rule as adulthood - exactly one must be active.
export const INFORMANT_CHILDHOOD_OPTIONS = [
  {
    key: 'baarsIVChildhoodInformant',
    label: 'BAARS-IV Informant (Childhood ADHD Symptoms)',
    description:
      'Informant report on childhood ADHD symptoms',
    mutuallyExclusiveWith: 'snapIVChildhoodInformant',
  },
  {
    key: 'snapIVChildhoodInformant',
    label: 'SNAP-IV Informant (Childhood ADHD Symptoms)',
    description:
      'Informant report on childhood ADHD symptoms',
    mutuallyExclusiveWith: 'baarsIVChildhoodInformant',
  },
];

/**
 * Rating scales on the CYP ADHD pathway.
 *
 * Unlike the adult pathways, these are not org toggles. snapIV26Informant
 * is the SNAP-IV 26: the parent or carer and the school representative both
 * rate the child's current ADHD symptoms. The CYP form registry assigns
 * that form to every new informant, for every organisation. alwaysOn tells
 * the settings UI to show it as enabled and to refuse a switch that the
 * rest of the product would ignore.
 *
 * This is a different form from the adult childhood informant SNAP-IV
 * (snapIVChildhoodInformant), which rates behaviour aged 5 to 12 and is
 * one of two choices on the adult ADHD pathway.
 */
export const CYP_RATING_SCALE_OPTIONS = [
  {
    key: 'snapIV26Informant',
    label: 'SNAP-IV 26 (ADHD Symptoms)',
    description:
      'SNAP-IV 26. The parent or carer and the school representative rate the child\'s ADHD symptoms over the past 6 months. Assigned for every organisation.',
    alwaysOn: true,
  },
];

/**
 * Combined instrument groups for the settings panel.  Each entry has:
 *   - groupKey:     Identifier used for navigation
 *   - title:        Section heading shown in the UI
 *   - subtitle:     Short description under the heading
 *   - type:         'toggles' - all groups use the same toggle UI
 *   - firestoreKey: (informant groups only) the string field on the
 *                   instruments doc that stores the selected instrument
 *                   key.  Absent for patient scales, which are stored
 *                   as a boolean map at `patientScales`.
 *   - items:        Array of instrument config objects
 */
export const INSTRUMENT_GROUPS = [
  {
    groupKey: 'patientScales',
    title: 'Patient Rating Scales',
    subtitle: 'Rating scales assigned to patients',
    type: 'toggles',
    items: PATIENT_SCALE_OPTIONS,
  },
  {
    groupKey: 'informantAdulthood',
    title: 'Informant Instrument (Adulthood)',
    subtitle: 'Rating scale assigned to patient\'s adulthood informant',
    type: 'toggles',
    firestoreKey: 'informantAdulthoodInstrument',
    items: INFORMANT_ADULTHOOD_OPTIONS,
  },
  {
    groupKey: 'informantChildhood',
    title: 'Informant Instrument (Childhood)',
    subtitle: 'Rating scale assigned to patient\'s childhood informant',
    type: 'toggles',
    firestoreKey: 'informantChildhoodInstrument',
    items: INFORMANT_CHILDHOOD_OPTIONS,
  },
  {
    // Not editable. SNAP-IV 26 is created for every CYP ADHD informant
    // (see constants/cyp_adhd/registry.py). A toggle here would let an
    // admin turn it off in settings while new cases still received it.
    groupKey: 'cypInformantScales',
    title: 'Informant Rating Scale',
    subtitle: 'Rating scale assigned to the parent or carer and the school representative',
    type: 'toggles',
    readOnly: true,
    audience: 'informant',
    items: CYP_RATING_SCALE_OPTIONS,
  },
];

// ─── Per-pathway configuration ──────────────────────────────────────────
// These exports control which patient scales appear in each pathway's tab
// and which instrument groups (patient + informant sections) are rendered.

/**
 * MODULE_SCALE_SETS — patient scale keys available per assessment pathway.
 *
 * Determines which toggles appear in the Patient Rating Scales section when
 * editing a given pathway tab. Ordering reflects clinical priority (most
 * pathway-relevant scales listed first).
 *
 * Both sets overlap significantly because comorbidity screening often requires
 * scales from adjacent pathways (e.g. AQ-10 in ADHD, BAARS in Autism).
 *
 * cypAdhd is intentionally empty. The child does not fill in a rating scale.
 * SNAP-IV 26 is an informant scale and lives in CYP_RATING_SCALE_OPTIONS.
 */
export const MODULE_SCALE_SETS = {
  adultAdhd: [
    'asrs', 'baarsIVCurrent', 'baarsIVChildhood',
    'phq9', 'gad7', 'aq10', 'aq50',
    'ess', 'mdq', 'ybocs', 'core10', 'cnst', 'spat',
  ],
  adultAutism: [
    'catq', 'aq10', 'aq50', 'auditc',
    'phq9', 'gad7', 'core10', 'cnst', 'spat',
    'ybocs', 'ess', 'mdq',
    'asrs', 'baarsIVCurrent', 'baarsIVChildhood',
  ],
  cypAdhd: [],
};

/**
 * MODULE_INSTRUMENT_GROUPS — which group sections to show per pathway.
 *
 * adultAdhd shows all three groups (patient scales + two informant groups).
 * adultAutism only shows patient scales because informant forms are not yet
 * built for the autism pathway.
 * cypAdhd shows the always-on informant scale only. There is no patient
 * scale group, so the summary does not render a "0 patient rating scales"
 * line for a pathway where the child completes no scales.
 */
export const MODULE_INSTRUMENT_GROUPS = {
  adultAdhd: ['patientScales', 'informantAdulthood', 'informantChildhood'],
  adultAutism: ['patientScales'],
  cypAdhd: ['cypInformantScales'],
};

/**
 * Human-readable labels for assessment module keys.
 * Used by the pathway tab selector and the Rating Scales summary card.
 * A module missing from this map is shown as its storage key, which is
 * not a name an org admin can read. Add a label here whenever a pathway
 * is added to availableAssessmentModules.
 */
export const ASSESSMENT_MODULE_LABELS = {
  adultAdhd: 'Adult ADHD',
  adultAutism: 'Adult Autism',
  cypAdhd: 'CYP ADHD',
};

/**
 * Lines for the Rating Scales card on Provider Settings, one per pathway
 * the organisation has switched on.
 *
 * Adult ADHD and Adult Autism read the org's saved toggles. CYP ADHD does
 * not: SNAP-IV 26 is listed from CYP_RATING_SCALE_OPTIONS even when the
 * saved instruments map is missing, empty, or says the scale is off. The
 * form registry always creates that form, so the card must not imply the
 * pathway has no rating scales.
 *
 * @param {object} args
 * @param {object|null} args.instruments - module-keyed instruments from getOrgConfig
 * @param {string[]} args.availableModules - pathway keys the org offers
 * @param {string} [args.orgName] - used to hide org-restricted scales
 * @returns {Array<{
 *   moduleKey: string,
 *   moduleLabel: string,
 *   enabledPatientScales: Array<{key: string, label: string}>,
 *   enabledInformantLabels: string[],
 *   showPatientScales: boolean,
 *   hasInformantConfig: boolean,
 *   informantNoun: string,
 * }>}
 */
export function buildInstrumentSummaries({ instruments, availableModules, orgName = '' }) {
  if (!instruments) return [];

  const modules = availableModules?.length ? availableModules : ['adultAdhd'];
  const visiblePatientScales = PATIENT_SCALE_OPTIONS.filter(
    (item) => !item.restrictToOrgs || item.restrictToOrgs.includes(orgName)
  );

  return modules.map((moduleKey) => {
    const modConfig = instruments[moduleKey] || {};
    const groupKeys = MODULE_INSTRUMENT_GROUPS[moduleKey] || ['patientScales'];
    const showPatientScales = groupKeys.includes('patientScales');

    const enabledPatientScales = showPatientScales
      ? visiblePatientScales.filter((item) => modConfig.patientScales?.[item.key])
      : [];

    const enabledInformantLabels = [];
    if (groupKeys.includes('informantAdulthood') && modConfig.informantAdulthoodInstrument) {
      const match = INFORMANT_ADULTHOOD_OPTIONS.find(
        (opt) => opt.key === modConfig.informantAdulthoodInstrument
      );
      if (match) enabledInformantLabels.push(match.label);
    }
    if (groupKeys.includes('informantChildhood') && modConfig.informantChildhoodInstrument) {
      const match = INFORMANT_CHILDHOOD_OPTIONS.find(
        (opt) => opt.key === modConfig.informantChildhoodInstrument
      );
      if (match) enabledInformantLabels.push(match.label);
    }

    // Always-on CYP scales are not stored as a reliable boolean. Include
    // them whenever the pathway shows this group, including when the org
    // document has no cypAdhd instruments block at all.
    if (groupKeys.includes('cypInformantScales')) {
      CYP_RATING_SCALE_OPTIONS.forEach((item) => {
        if (item.alwaysOn || modConfig.patientScales?.[item.key]) {
          enabledInformantLabels.push(item.label);
        }
      });
    }

    const hasSelectableInformants = groupKeys.includes('informantAdulthood')
      || groupKeys.includes('informantChildhood');

    return {
      moduleKey,
      moduleLabel: ASSESSMENT_MODULE_LABELS[moduleKey] || moduleKey,
      enabledPatientScales,
      enabledInformantLabels,
      showPatientScales,
      hasInformantConfig: hasSelectableInformants || groupKeys.includes('cypInformantScales'),
      // Adult pathways pick one instrument per life stage. CYP has a single
      // rating scale, so the card names it that way.
      informantNoun: hasSelectableInformants ? 'informant instrument' : 'informant rating scale',
    };
  });
}
